import type { ValidatedPinUser } from 'washday-sdk';
import type { CashierPinRequestOptions } from '../../hooks/useRequestCashierPinValidation';
import { Dispatch, SetStateAction, memo, useMemo } from 'react';
import { IOrder } from '../../interfaces/Order';
import { ActionIcon, Grid, Menu, rem } from '@mantine/core';
import moment from 'moment';
import { type MRT_PaginationState } from 'mantine-react-table';
import { useTranslation } from 'react-i18next';
import Table from '../Table';
import { formatMoneyString, getCustomerFullName } from '../../utils/strings';
import {
  IconCircleCheck,
  IconCurrencyDollar,
  IconEdit,
  IconMenu2,
  IconShirt,
  IconTrash
} from '@tabler/icons-react';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import { useHistory } from 'react-router-dom';
import { modals } from '@mantine/modals';
import AmountDuePaymentModal from '../OrderModals/AmountDuePaymentModal';
import { buildOrderFolio, getOrderCardColorsByAmountDueAndStatus } from '../../utils/ordersUtils';
import { getOrderAmountDue } from '@washday/utilities';
import { IStore } from '../../interfaces/Store';
import { getAllPieces } from '../../utils/helpers/getAllPieces';
import useCanEditOrders from '../../hooks/useCanEditOrders';

const ActionMenu = ({
  order,
  searchParamStoreId,
  showPaymentLinesModal,
  showOrderProductsModal,
  onOrderCancelled,
  onOrderCollected,
  onOrderPayAndCollect,
  onAskForPin,
  onMercadoPagoSelected,
  allowMercadoPago
}) => {
  const { t } = useTranslation(['OrdersPage', 'FieldName']);
  const history = useHistory();
  const canEditOrders = useCanEditOrders();
  const amountDue = getOrderAmountDue(order);
  const hasAmountDue = amountDue > 0;

  const onCollectOrder = async ({
    order,
    hasAmountDue,
    amountDue
  }: {
    order: IOrder;
    hasAmountDue: boolean;
    amountDue: number;
  }) => {
    try {
      const sourceStoreId = typeof order.createdIn === 'string' ? order.createdIn : order.createdIn?._id || '';
      const pinUser = await onAskForPin({ storeId: sourceStoreId });
      return !hasAmountDue
        ? ConfirmationModal({
            title: t('Notifications:CRUD.collectedConfirmation'),
            onConfirm: () =>
              onOrderCollected({
                orderId: order._id || '',
                sequence: order.sequence || '',
                storeId: sourceStoreId,
                pinUserId: pinUser?._id,
                ...(pinUser?.pinValidationToken ? { pinValidationToken: pinUser.pinValidationToken } : {})
              }),
            onCancel: () => {}
          })
        : modals.open({
            modalId: 'paidAndCollectedOrder',
            title: t('Notifications:CRUD.collectedConfirmation'),
            children: (
              <AmountDuePaymentModal
                order={order}
                close={() => modals.close('paidAndCollectedOrder')}
                storeId={sourceStoreId}
                onSubmit={onOrderPayAndCollect}
                amountDue={amountDue}
                pinUser={pinUser}
                allowMercadoPago={allowMercadoPago}
                onMercadoPagoSelected={({ paymentMethod, cashierBoxId, amount, paymentDate, collectWithAmountDue, pinUserId, pinValidationToken }) =>
                  onMercadoPagoSelected?.({
                    order,
                    paymentMethod,
                    cashierBoxId,
                    amount,
                    paymentDate,
                    collectWithAmountDue,
                    pinUserId,
                    pinValidationToken,
                    storeId: sourceStoreId
                  })
                }
              />
            )
          });
    } catch (e) {
      console.log(e);
    }
  };
  
  const rowActions = [
    <Menu.Item
      onClick={() => showPaymentLinesModal(order)}
      leftSection={<IconCurrencyDollar style={{ width: rem(14), height: rem(14) }} />}>
      {t('OrdersPage:paymentLinesModal.tooltip')}
    </Menu.Item>,
    <Menu.Item
      onClick={() => showOrderProductsModal(order)}
      leftSection={<IconShirt style={{ width: rem(14), height: rem(14) }} />}>
      {t('OrdersPage:productsModal.tooltip')}
    </Menu.Item>,
    <Menu.Item
      disabled={!canEditOrders}
      onClick={() =>
        history.push(`/orders/${order._id}?storeId=${searchParamStoreId}`, {
          storeID: searchParamStoreId
        })
      }
      leftSection={<IconEdit style={{ width: rem(14), height: rem(14) }} />}>
      {t('CommonButtons:edit')}
    </Menu.Item>,

    <Menu.Item
      onClick={() => onCollectOrder({ order, hasAmountDue, amountDue })}
      leftSection={<IconCircleCheck style={{ width: rem(14), height: rem(14) }} />}>
      Pedido Entregado
    </Menu.Item>,
    <Menu.Item
      onClick={() =>
        ConfirmationModal({
          title: t('Notifications:CRUD.cancelConfirmation'),
          onConfirm: () =>
            onOrderCancelled({
              orderId: order._id || '',
              sequence: order.sequence || '',
              storeId: order.createdIn?._id || ''
            }),
          onCancel: () => {}
        })
      }
      leftSection={<IconTrash style={{ width: rem(14), height: rem(14) }} />}>
      Cancelar Pedido
    </Menu.Item>
  ];

  return (
    <Menu shadow="md" width={200}>
      <Menu.Target>
        <ActionIcon variant="transparent">
          <IconMenu2 style={{ width: '70%', height: '70%' }} stroke={1.5} />
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown>{...rowActions}</Menu.Dropdown>
    </Menu>
  );
};

export default memo(function OrdersTableView({
  orders = [],
  totalRowsCount = 0,
  pages = 0,
  isError = false,
  isLoading = false,
  pagination,
  setPagination,
  searchParamStoreId,
  onOrderCollected,
  onOrderCancelled,
  onSendEmailReceipt,
  onPrintTicket,
  showPaymentLinesModal,
  showOrderProductsModal,
  onOrderPayAndCollect,
  onAskForPin,
  onMercadoPagoSelected,
  allowMercadoPago,
  amountDueTotal
}: {
  orders: any;
  totalRowsCount: any;
  pages: any;
  isError: boolean;
  isLoading: boolean;
  pagination: MRT_PaginationState;
  setPagination: Dispatch<SetStateAction<MRT_PaginationState>>;
  searchParamStoreId: string;
  onOrderPayAndCollect: ({
    orderId,
    sequence,
    storeId,
    paymentMethod,
    cashierBoxId,
    amount,
    paymentDate,
    collectWithAmountDue,
    pinUserId,
    attemptId
  }: {
    orderId?: string;
    sequence: string;
    storeId: string;
    paymentMethod: string;
    cashierBoxId: string;
    amount: number;
    paymentDate: string;
    collectWithAmountDue: boolean;
    pinUserId?: string;
    pinValidationToken?: string;
    attemptId?: string | null;
  }) => Promise<void>;
  onMercadoPagoSelected?: (args: {
    order: IOrder;
    storeId: string;
    paymentMethod: string;
    cashierBoxId: string;
    amount: number;
    paymentDate: string;
    collectWithAmountDue: boolean;
    pinUserId?: string;
    pinValidationToken?: string;
  }) => void;
  allowMercadoPago?: boolean;
  onOrderCollected: ({
    orderId,
    sequence,
    storeId,
    pinUserId
  }: {
    orderId?: string;
    sequence: string;
    storeId: string;
    pinUserId?: string;
    pinValidationToken?: string;
  }) => Promise<void>;
  onOrderCancelled: ({
    orderId,
    sequence,
    storeId
  }: {
    orderId?: string;
    sequence: string;
    storeId: string;
  }) => Promise<void>;
  onSendEmailReceipt: (orderId: string) => Promise<void>;
  onPrintTicket: (order: IOrder) => Promise<void>;
  showPaymentLinesModal: (order: IOrder) => void;
  showOrderProductsModal: (order: IOrder) => void;
  onAskForPin: (options?: CashierPinRequestOptions) => Promise<ValidatedPinUser | null>;
  amountDueTotal?: number;
}) {
  const { t } = useTranslation(['FieldName']);
  const amountDuePageTotal = useMemo(
    () =>
      (orders || []).reduce((acc: number, order: IOrder) => {
        return acc + getOrderAmountDue(order);
      }, 0),
    [orders]
  );
  const amountDueFooter = amountDueTotal ?? amountDuePageTotal;
  const columns = [
    {
      header: t('FieldName:actions'),
      size: 50, //small column
      accessorFn: (row) => {
        return (
          <ActionMenu
            order={row}
            searchParamStoreId={searchParamStoreId}
            showPaymentLinesModal={showPaymentLinesModal}
            showOrderProductsModal={showOrderProductsModal}
            onOrderCancelled={onOrderCancelled}
            onOrderCollected={onOrderCollected}
            onOrderPayAndCollect={onOrderPayAndCollect}
            onAskForPin={onAskForPin}
            onMercadoPagoSelected={onMercadoPagoSelected}
            allowMercadoPago={allowMercadoPago}
          />
        );
      }
    },
    {
      accessorFn: (row) => buildOrderFolio(row?.folioPrefix, row?.sequence),
      header: t('FieldName:order')
    },
    {
      accessorFn: (row) => moment(row.deliveryInfo.date).format('DD/MM/YYYY hh:mm'),
      header: t('FieldName:deliveryDate')
    },
    {
      accessorFn: (row) => getCustomerFullName(row.customer.name, row.customer.lastName),
      header: t('FieldName:customer')
    },
    {
      accessorFn: (row) => {
        const orderProducts = row?.products || [];
        const orderBuyAndGerProducts = row?.buyAndGetProducts || [];
        const allPieces = getAllPieces(orderProducts, orderBuyAndGerProducts);
        return allPieces;
      },
      header: t('FieldName:pieces')
    },
    {
      accessorFn: (row) => formatMoneyString((row.total || 0).toFixed(2)),
      header: t('FieldName:total')
    },
    {
      accessorFn: (row) => formatMoneyString(getOrderAmountDue(row).toFixed(2)),
      header: t('FieldName:amountDue'),
      Footer: () => formatMoneyString((amountDueFooter || 0).toFixed(2))
    },
    {
      accessorFn: (row) => row.notes,
      header: t('FieldName:notes')
    }
  ];

  return (
    <>
      <Grid gutter="md">
        <Grid.Col span={12}>
          <Table
            tableColumns={columns}
            pagination={pagination}
            setPagination={setPagination}
            tableData={orders}
            isError={isError}
            paginationIsLoading={isLoading}
            numPages={pages}
            totalRowsCount={totalRowsCount}
            overrideTableConfigs={{
              mantineTableBodyRowProps: ({ row }: { row: any }) => {
                const amountDue = getOrderAmountDue(row.original);
                const customColor = getOrderCardColorsByAmountDueAndStatus({
                  amountDue,
                  highlightOrderCardsByPaymentPending: (row.original?.createdIn as Partial<IStore>)
                    ?.ordersDisplayConfig?.highlightOrderCardsByPaymentPending,
                  status: row.original?.status
                });

                return {
                  style: {
                    backgroundColor: customColor?.headerBackgroundColor,
                    color: customColor?.headerTextColor
                  }
                };
              }
            }}
          />
        </Grid.Col>
      </Grid>
    </>
  );
});
